import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  Tooltip,
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import IosShareIcon from "@mui/icons-material/IosShare";
import * as XLSX from "xlsx";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import HouseIcon from "@mui/icons-material/House";
import { useLocation, useNavigate } from "react-router-dom";

// =====================================================================
// Scrolls, but paints no scrollbar. Applied to the two panels so each one
// scrolls on its own without a track eating into the layout.
// The panels stay fixed-height only from `md` up; on phones the Grid stacks
// and the page scrolls normally, which is what you want there.
// =====================================================================
const HIDDEN_SCROLLBAR = {
  overflowY: "auto",
  scrollbarWidth: "none", // Firefox
  msOverflowStyle: "none", // IE / legacy Edge
  "&::-webkit-scrollbar": { display: "none" }, // Chrome / Safari / Edge
  // Keep momentum scrolling on trackpads/touch
  WebkitOverflowScrolling: "touch",
};

// =====================================================================
// Feedback rating scale — 1 (worst) .. 5 (best).
// Each level carries a label as well as a colour: colour alone is not a
// reliable signal (colour-blind users, greyscale printing).
// =====================================================================
const RATING_SCALE = [
  { value: 1, label: "Very Bad", short: "VB", color: "#d32f2f" },
  { value: 2, label: "Bad", short: "B", color: "#f57c00" },
  { value: 3, label: "Average", short: "A", color: "#fbc02d" },
  { value: 4, label: "Good", short: "G", color: "#7cb342" },
  { value: 5, label: "Very Good", short: "VG", color: "#2e7d32" },
];

const getRating = (value) =>
  RATING_SCALE.find((r) => r.value === Number(value)) || null;

// Most recent feedback for a driver row, used for the list dot.
const getLatestFeedback = (driver) => {
  const list = driver?.feedbacks;
  if (!Array.isArray(list) || list.length === 0) return null;
  return list.reduce((latest, fb) =>
    String(fb.date || "") > String(latest.date || "") ? fb : latest,
  );
};

// =====================================================================
// Date picker — same calendar as the dashboards, time controls omitted.
// =====================================================================
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAY_HEADERS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonthDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = firstDay - 1; i >= 0; i--)
    cells.push({
      date: new Date(year, month - 1, prevMonthDays - i),
      outside: true,
    });
  for (let d = 1; d <= daysInMonth; d++)
    cells.push({ date: new Date(year, month, d), outside: false });
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++)
    cells.push({ date: new Date(year, month + 1, d), outside: true });
  return cells;
}

function toYMD(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromYMD(str) {
  if (!str) return null;
  const [y, m, d] = String(str).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function fmtDMY(ymd) {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).split("-");
  if (!y || !m || !d) return String(ymd);
  return `${d}-${m}-${y}`;
}

const SingleDatePicker = ({
  date,
  onDateChange,
  label = "Select date",
  maxDate = null,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const today = new Date();
  const [calYear, setCalYear] = useState(
    () => fromYMD(date)?.getFullYear() ?? today.getFullYear(),
  );
  const [calMonth, setCalMonth] = useState(
    () => fromYMD(date)?.getMonth() ?? today.getMonth(),
  );

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownHeight = 260;
    const dropdownWidth = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= dropdownHeight
        ? rect.bottom + window.scrollY + 4
        : rect.top + window.scrollY - dropdownHeight - 4;
    const spaceRight = window.innerWidth - rect.left;
    const left =
      spaceRight >= dropdownWidth
        ? rect.left + window.scrollX
        : rect.right + window.scrollX - dropdownWidth;
    setDropdownPos({ top, left });
  };

  const handleOpen = () => {
    updatePosition();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open]);

  const selectedDateObj = fromYMD(date);

  const prevMonth = () => {
    if (calMonth === 0) {
      setCalMonth(11);
      setCalYear((y) => y - 1);
    } else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) {
      setCalMonth(0);
      setCalYear((y) => y + 1);
    } else setCalMonth((m) => m + 1);
  };

  const cells = buildCalendarDays(calYear, calMonth);

  const dropdownEl = open
    ? ReactDOM.createPortal(
        <Paper
          ref={dropdownRef}
          elevation={8}
          sx={{
            position: "absolute",
            top: dropdownPos.top,
            left: dropdownPos.left,
            zIndex: 99999,
            p: 1.5,
            borderRadius: 2,
            width: 240,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <IconButton size="small" onClick={prevMonth} sx={{ p: 0.3 }}>
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, fontSize: "0.78rem" }}
            >
              {MONTH_NAMES[calMonth]} {calYear}
            </Typography>
            <IconButton size="small" onClick={nextMonth} sx={{ p: 0.3 }}>
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7,1fr)",
              mb: 0.3,
            }}
          >
            {DAY_HEADERS.map((d) => (
              <Typography
                key={d}
                variant="caption"
                sx={{
                  textAlign: "center",
                  fontWeight: 700,
                  color: "#888",
                  fontSize: "0.62rem",
                }}
              >
                {d}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7,1fr)",
              gap: 0,
            }}
          >
            {cells.map((cell, idx) => {
              const { date: cellDate, outside } = cell;
              const isSelected = isSameDay(cellDate, selectedDateObj);
              const isDisabled =
                outside || (maxDate && toYMD(cellDate) > maxDate);
              let bg = "transparent";
              let color = isDisabled ? "#ccc" : "#222";
              let fontWeight = 400;
              if (isSelected) {
                bg = "#1976d2";
                color = "#fff";
                fontWeight = 700;
              }
              return (
                <Box
                  key={idx}
                  onClick={() => {
                    if (isDisabled) return;
                    onDateChange(toYMD(cellDate));
                    setOpen(false);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 26,
                    bgcolor: bg,
                    borderRadius: "50%",
                    cursor: isDisabled ? "default" : "pointer",
                    "&:hover": !isDisabled
                      ? { bgcolor: isSelected ? "#1565c0" : "#bbdefb" }
                      : {},
                    transition: "background 0.1s",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight,
                      color,
                      lineHeight: 1,
                      userSelect: "none",
                    }}
                  >
                    {cellDate.getDate()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: "0.72rem", py: 0.3, minWidth: 0 }}
              onClick={() => {
                onDateChange("");
                setOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: "0.72rem", py: 0.3, minWidth: 0 }}
              onClick={() => {
                onDateChange(toYMD(new Date()));
                setOpen(false);
              }}
            >
              Today
            </Button>
          </Box>
        </Paper>,
        document.body,
      )
    : null;

  return (
    <Box ref={triggerRef} sx={{ display: "inline-block", width: "100%" }}>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          border: open ? "1.5px solid #1976d2" : "1px solid rgba(0,0,0,0.23)",
          borderRadius: "4px",
          px: 1,
          py: "7px",
          cursor: "pointer",
          bgcolor: "#fff",
          "&:hover": { borderColor: "#333" },
        }}
      >
        <CalendarTodayIcon
          sx={{ fontSize: 14, color: open ? "#1976d2" : "#888", flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.85rem",
            color: date ? "#222" : "#999",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
          }}
        >
          {date ? fmtDMY(date) : label}
        </Typography>
      </Box>
      {dropdownEl}
    </Box>
  );
};

// =====================================================================
// Rating picker — red .. green pills
// =====================================================================
const RatingSelector = ({ value, onChange }) => (
  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
    {RATING_SCALE.map((r) => {
      const active = Number(value) === r.value;
      return (
        <Tooltip key={r.value} title={r.label} arrow>
          <Box
            onClick={() => onChange(active ? "" : r.value)}
            sx={{
              px: 1,
              height: 28,
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              borderRadius: 1,
              cursor: "pointer",
              userSelect: "none",
              border: "1px solid",
              borderColor: active ? r.color : "rgba(0,0,0,0.18)",
              bgcolor: active ? r.color : "transparent",
              transition: "all 0.12s",
              "&:hover": { borderColor: r.color },
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: active ? "#fff" : r.color,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: active ? 700 : 500,
                color: active ? "#fff" : "text.secondary",
                lineHeight: 1,
              }}
            >
              {r.label}
            </Typography>
          </Box>
        </Tooltip>
      );
    })}
  </Box>
);

// A compact coloured chip for list/table display.
const RatingChip = ({ value, showLabel = true }) => {
  const r = getRating(value);
  if (!r) return null;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          bgcolor: r.color,
          flexShrink: 0,
        }}
      />
      {showLabel && (
        <Typography
          sx={{ fontSize: "0.78rem", color: r.color, fontWeight: 600 }}
        >
          {r.label}
        </Typography>
      )}
    </Box>
  );
};

const emptyDriver = {
  id: null,
  driver_code: "",
  name: "",
  phone: "",
  status: "A",
  dob: "",
  age: "",
  bloodGroup: "",
  experience: "",
  qualification: "",
  joinedDate: "",
  blockedFrom: "",
  blockedTo: "",
  languages: [],
  documents: [],
  feedbacks: [],
  vehicle_id: null,
};

export default function DriverMaster() {
  const navigate = useNavigate();
  const location = useLocation();
  const preloadDriverId = location.state?.preloadDriverId; // id passed from ReminderMaster
  const returnTo = location.state?.returnTo;

  const { userInfo } = useContext(AutoTaskAuthContext) || {};
  const store_id = userInfo?.store_id;
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [search, setSearch] = useState("");
  const [formData, setFormData] = useState(emptyDriver);
  const [languageInput, setLanguageInput] = useState("");
  const [refresh, setRefresh] = useState(true);
  const [newDocument, setNewDocument] = useState({
    name: "",
    number: "",
    file: null,
    fileName: "",
    renewalDate: "",
  });
  const [editingDocId, setEditingDocId] = useState(null);

  const EMPTY_DOCUMENT = {
    name: "",
    number: "",
    file: null,
    fileName: "",
    renewalDate: "",
  };

  // Document fields are staged in `newDocument` until Add/Update is pressed,
  // so they are unsaved work that the isEdited flag never sees on its own.
  const hasPendingDocument =
    editingDocId !== null ||
    Boolean(
      newDocument.name ||
        newDocument.number ||
        newDocument.renewalDate ||
        newDocument.file ||
        newDocument.fileName,
    );

  const resetDocumentEntry = () => {
    setEditingDocId(null);
    setNewDocument({ ...EMPTY_DOCUMENT });
  };

  /* ---------------- FEEDBACK ENTRY (staged like documents) ---------------- */
  const EMPTY_FEEDBACK = { date: "", rating: "", remarks: "" };

  const [newFeedback, setNewFeedback] = useState({ ...EMPTY_FEEDBACK });
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const [ratingFilter, setRatingFilter] = useState(null);

  const hasPendingFeedback =
    editingFeedbackId !== null ||
    Boolean(newFeedback.date || newFeedback.rating || newFeedback.remarks);

  const resetFeedbackEntry = () => {
    setEditingFeedbackId(null);
    setNewFeedback({ ...EMPTY_FEEDBACK });
  };

  // Both staged child entries — cleared together whenever the form as a whole
  // is swapped out (driver switch, add, cancel, save).
  const resetChildEntries = () => {
    resetDocumentEntry();
    resetFeedbackEntry();
    setRatingFilter(null); // a filter from the previous driver would mislead
  };

  // Date + rating are required; remarks are optional so a quick score can be
  // logged without typing anything.
  const isFeedbackComplete = Boolean(newFeedback.date && newFeedback.rating);

  const handleFeedbackChange = (field, value) => {
    setNewFeedback((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddFeedback = () => {
    if (!isFeedbackComplete) {
      setSnackbar({
        open: true,
        message: "Pick a date and a rating first",
        severity: "warning",
      });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      feedbacks: [
        ...(prev.feedbacks || []),
        {
          id: Date.now(),
          date: newFeedback.date,
          rating: Number(newFeedback.rating),
          remarks: newFeedback.remarks || "",
        },
      ],
    }));
    resetFeedbackEntry();
    setIsEdited(true);
  };

  const handleEditFeedback = (fb) => {
    setEditingFeedbackId(fb.feedback_id || fb.id);
    setNewFeedback({
      date: fb.date || "",
      rating: fb.rating || "",
      remarks: fb.remarks || "",
    });
  };

  const handleUpdateFeedback = () => {
    if (!isFeedbackComplete) {
      setSnackbar({
        open: true,
        message: "Pick a date and a rating first",
        severity: "warning",
      });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      feedbacks: (prev.feedbacks || []).map((fb) =>
        (fb.feedback_id || fb.id) === editingFeedbackId
          ? {
              ...fb,
              date: newFeedback.date,
              rating: Number(newFeedback.rating),
              remarks: newFeedback.remarks || "",
            }
          : fb,
      ),
    }));
    resetFeedbackEntry();
    setIsEdited(true);
  };

  const handleDeleteFeedback = (feedbackId) => {
    setFormData((prev) => ({
      ...prev,
      feedbacks: (prev.feedbacks || []).filter(
        (fb) => (fb.feedback_id || fb.id) !== feedbackId,
      ),
    }));
    if (editingFeedbackId === feedbackId) resetFeedbackEntry();
    setIsEdited(true);
  };

  // Newest first, so the most recent judgement is what you see.
  const sortedFeedbacks = useMemo(
    () =>
      [...(formData.feedbacks || [])].sort((a, b) =>
        String(b.date || "").localeCompare(String(a.date || "")),
      ),
    [formData.feedbacks],
  );

  // Count, average and per-rating tallies — drives the summary and the filter
  // strip so the whole history can be judged without reading a single remark.
  const feedbackStats = useMemo(() => {
    const counts = RATING_SCALE.reduce(
      (acc, r) => ({ ...acc, [r.value]: 0 }),
      {},
    );
    let sum = 0;

    sortedFeedbacks.forEach((fb) => {
      const rating = Number(fb.rating);
      if (counts[rating] !== undefined) counts[rating] += 1;
      sum += rating;
    });

    const total = sortedFeedbacks.length;
    const average = total > 0 ? (sum / total).toFixed(1) : null;

    return {
      total,
      counts,
      average,
      // Round to the nearest scale step for the label + colour.
      averageRating: total > 0 ? getRating(Math.round(sum / total)) : null,
    };
  }, [sortedFeedbacks]);

  // Exports exactly what is on screen, so a rating filter carries into the
  // file rather than silently exporting rows the user cannot see.
  const handleFeedbackExcelExport = () => {
    if (visibleFeedbacks.length === 0) {
      setSnackbar({
        open: true,
        message: "No feedback to export",
        severity: "warning",
      });
      return;
    }

    const driverLabel = formData.name || selectedDriver?.name || "Driver";
    const driverCode = formData.driver_code || selectedDriver?.driver_code || "";

    const rows = visibleFeedbacks.map((fb, idx) => ({
      "Sl No": idx + 1,
      "Driver Code": driverCode || "-",
      "Driver Name": driverLabel,
      Date: fmtDMY(fb.date) || "-",
      // Both the word and the number: the label reads at a glance, the score
      // sorts and averages in Excel.
      Rating: getRating(fb.rating)?.label || "-",
      Score: Number(fb.rating) || "",
      Feedback: fb.remarks || "",
    }));

    // Average of what was exported, so the sheet is self-explanatory.
    const exportedAvg =
      rows.length > 0
        ? (
            visibleFeedbacks.reduce((a, f) => a + (Number(f.rating) || 0), 0) /
            visibleFeedbacks.length
          ).toFixed(1)
        : "";

    rows.push({
      "Sl No": "",
      "Driver Code": "",
      "Driver Name": "",
      Date: "",
      Rating: "AVERAGE",
      Score: exportedAvg,
      Feedback:
        ratingFilter === null
          ? `${visibleFeedbacks.length} entr${
              visibleFeedbacks.length === 1 ? "y" : "ies"
            }`
          : `Filtered: ${getRating(ratingFilter)?.label || ""} only`,
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 7 }, // Sl No
      { wch: 13 }, // Driver Code
      { wch: 22 }, // Driver Name
      { wch: 12 }, // Date
      { wch: 12 }, // Rating
      { wch: 7 }, // Score
      { wch: 60 }, // Feedback
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Driver Feedback");

    const stem = `Driver_Feedback_${[driverCode, driverLabel]
      .filter(Boolean)
      .join("_")}`.replace(/[^a-zA-Z0-9_]/g, "_");
    XLSX.writeFile(workbook, `${stem}.xlsx`);
  };

  const visibleFeedbacks = useMemo(
    () =>
      ratingFilter === null
        ? sortedFeedbacks
        : sortedFeedbacks.filter((fb) => Number(fb.rating) === ratingFilter),
    [sortedFeedbacks, ratingFilter],
  );

  // Unsaved-changes prompt. Holds the action to replay once the user decides,
  // since the dialog can't block the way window.confirm did.
  const [discardPrompt, setDiscardPrompt] = useState(null);

  const guardUnsavedChanges = (action, actionLabel = "") => {
    if (!isEdited && !hasPendingDocument && !hasPendingFeedback) {
      action();
      return;
    }
    setDiscardPrompt({ actionLabel, onDiscard: action });
  };

  const handleDiscardConfirm = () => {
    const action = discardPrompt?.onDiscard;
    setDiscardPrompt(null);
    action?.();
  };

  const [isAddMode, setIsAddMode] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isEdited, setIsEdited] = useState(false);

  // vehicle state
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);

  // API Call Function
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/drivers/store/${store_id} `;
        const response = await axios.get(url);
        const { success } = response?.data;
        if (success) {
          setDrivers(response?.data?.data);
        }
      } catch (error) {}
    };
    const fetchVehicles = async () => {
      try {
        setIsLoadingVehicles(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/travel/vehicles/store/${store_id}`,
        );
        setVehicles(res.data?.data || []);
      } catch {
        setSnackbar({
          open: true,
          message: "Failed to load vehicles",
          severity: "error",
        });
      } finally {
        setIsLoadingVehicles(false);
      }
    };

    fetchDrivers();
    fetchVehicles();
  }, [refresh, store_id]);

  // Auto-load a specific driver when arriving from ReminderMaster
  useEffect(() => {
    if (preloadDriverId && drivers.length > 0) {
      const driver = drivers.find((d) => d.id === preloadDriverId);
      if (driver) handleDriverSelect(driver);
    }
  }, [preloadDriverId, drivers]);

  const applyDriverSelect = (driver) => {
    resetChildEntries();
    setSelectedDriver(driver);
    setFormData({ ...driver });
    setIsAddMode(false);
    setIsEdited(false);

    // Auto-fill vehicle if driver has vehicle_id
    if (driver.vehicle_id) {
      const linkedVehicle = vehicles.find(
        (v) => v.vehicle_id === driver.vehicle_id,
      );
      if (linkedVehicle) {
        setSelectedVehicle(linkedVehicle);
      }
    } else {
      setSelectedVehicle(null);
    }
  };

  const handleDriverSelect = (driver) => {
    guardUnsavedChanges(() => applyDriverSelect(driver), "switch driver");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setIsEdited(true);
  };

  const handleAddLanguage = () => {
    if (
      languageInput.trim() &&
      !formData.languages.includes(languageInput.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        languages: [...prev.languages, languageInput.trim()],
      }));
      setLanguageInput("");
      setIsEdited(true);
    }
  };

  const handleDeleteLanguage = (langToDelete) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((lang) => lang !== langToDelete),
    }));
    setIsEdited(true);
  };

  // Update handleDocumentChange to handle file uploads
  const handleDocumentChange = (field, value) => {
    if (field === "file") {
      setNewDocument((prev) => ({
        ...prev,
        file: value,
        fileName: value ? value.name : "",
      }));
    } else {
      setNewDocument((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleAddDocument = () => {
    if (newDocument.name && newDocument.number && newDocument.renewalDate) {
      const doc = {
        id: Date.now(),
        name: newDocument.name,
        number: newDocument.number,
        file: newDocument.fileName || "No file",
        renewalDate: newDocument.renewalDate,
      };
      setFormData((prev) => ({
        ...prev,
        documents: [...prev.documents, doc],
      }));
      resetDocumentEntry();
      setIsEdited(true);
    } else {
      setSnackbar({
        open: true,
        message: "Please fill all required document fields",
        severity: "warning",
      });
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDocId(doc.doc_id || doc.id);
    setNewDocument({
      name: doc.name,
      number: doc.number,
      file: null,
      fileName: doc.file,
      renewalDate: doc.renewalDate,
    });
  };

  const handleUpdateDocument = () => {
    if (newDocument.name && newDocument.number && newDocument.renewalDate) {
      setFormData((prev) => ({
        ...prev,
        documents: prev.documents.map((doc) =>
          (doc.doc_id || doc.id) === editingDocId
            ? {
                ...doc,
                name: newDocument.name,
                number: newDocument.number,
                file: newDocument.fileName || doc.file,
                renewalDate: newDocument.renewalDate,
              }
            : doc,
        ),
      }));
      resetDocumentEntry();
      setIsEdited(true);
    } else {
      setSnackbar({
        open: true,
        message: "Please fill all required document fields",
        severity: "warning",
      });
    }
  };

  const handleCancelEditDocument = () => {
    resetDocumentEntry();
  };

  const handleDeleteDocument = (docId) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter(
        (doc) => (doc.doc_id || doc.id) !== docId,
      ),
    }));
    setIsEdited(true);
  };

  const applyAddDriver = () => {
    resetChildEntries();
    setIsAddMode(true);
    setFormData({ ...emptyDriver });
    setSelectedDriver(null);
    setSelectedVehicle(null);
    setIsEdited(false);
  };

  const handleAddDriver = () => {
    guardUnsavedChanges(applyAddDriver, "add a new driver");
  };

  //Handle Create Driver
  const handleCreateDriver = async () => {
    // Validation
    if (!formData.driver_code || !formData.name || !formData.phone) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields (Code, Name, Phone)",
        severity: "error",
      });
      return;
    }

    try {
      if (isAddMode) {
        // ✅ Create driver
        const payload = {
          driver: {
            ...formData,
            store_id: store_id,
            vehicle_id: formData.vehicle_id || null,
          },
        };

        const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/drivers/create`;
        const response = await axios.post(url, payload);

        if (response.data?.success) {
          const savedDriver = response.data.data;

          setDrivers((prev) => [...prev, savedDriver]);
          setSelectedDriver(null);
          setFormData(emptyDriver);
          setSelectedVehicle(null);
          setIsEdited(false);
          resetChildEntries();

          setSnackbar({
            open: true,
            message: "Driver added successfully!",
            severity: "success",
          });

          setFormData(emptyDriver);
          setRefresh((prev) => !prev);
        } else {
          setSnackbar({
            open: true,
            message: response?.data?.statusMessage || "Failed to add driver",
            severity: "error",
          });
        }
      } else {
        // ✅ Update driver
        const payload = {
          driver: formData,
          vehicle_id: formData.vehicle_id || null,
        };

        const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/drivers/${selectedDriver.id}`;
        const response = await axios.put(url, payload);

        if (response.data?.success) {
          const updatedDriver = response.data.data;

          setDrivers((prev) =>
            prev.map((driver) =>
              driver.id === updatedDriver.id ? updatedDriver : driver,
            ),
          );

          setSelectedDriver(null);
          setFormData(emptyDriver);
          setSelectedVehicle(null);
          setIsEdited(false);
          setIsAddMode(true);
          resetChildEntries();
          setRefresh((prev) => !prev);

          setSnackbar({
            open: true,
            message: "Driver updated successfully!",
            severity: "success",
          });

          // If we came from ReminderMaster, navigate back so it can refresh documents
          if (returnTo) {
            setTimeout(() => {
              navigate(returnTo, { state: { fromMasterEdit: true } });
            }, 800); // small delay so the snackbar is visible before leaving
          }
        } else {
          setSnackbar({
            open: true,
            message: response?.data?.statusMessage || "Failed to update driver",
            severity: "error",
          });
        }
      }
    } catch (error) {
      console.error("API Error:", error);

      const apiMessage =
        error?.response?.data?.statusMessage ||
        error?.message ||
        "Failed to save driver. Please try again.";

      setSnackbar({
        open: true,
        message: apiMessage,
        severity: "error",
      });
    }
  };

  const applyCancel = () => {
    resetChildEntries();
    if (isAddMode) {
      if (drivers.length > 0) {
        setSelectedDriver(drivers[0]);
        setFormData({ ...drivers[0] });
      }
      setIsAddMode(false);
    } else if (selectedDriver) {
      setFormData({ ...selectedDriver });
    }

    setIsEdited(false);
    setSelectedDriver(null);
    setFormData(emptyDriver);
    setSelectedVehicle(null);
  };

  const handleCancel = () => {
    guardUnsavedChanges(applyCancel, "discard this form");
  };

  const handleDeleteDriver = async () => {
    const driverId = selectedDriver?.id;

    if (!driverId) {
      setSnackbar({
        open: true,
        message: "No driver selected",
        severity: "error",
      });
      return;
    }

    // Confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to delete driver "${selectedDriver?.name}"? This will also delete all related documents.`,
    );

    if (!confirmDelete) {
      return;
    }

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/travel/drivers/${driverId}`;

    try {
      const response = await axios.delete(url);

      if (response.data?.success) {
        // Remove driver from list
        setDrivers((prev) => prev.filter((driver) => driver.id !== driverId));

        // Clear selected driver and form
        setSelectedDriver(null);
        setFormData({
          driver_code: "",
          name: "",
          phone: "",
          dob: "",
          bloodGroup: "",
          experience: "",
          joinedDate: "",
          qualification: "",
          status: "A",
          languages: [],
          blockedFrom: "",
          blockedTo: "",
          documents: [],
          vehicle_id: null,
        });
        setSelectedVehicle(null);

        // Refresh list
        setRefresh((prev) => !prev);

        setSnackbar({
          open: true,
          message:
            response.data.statusMessage || "Driver deleted successfully!",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: response.data.statusMessage || "Failed to delete driver",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Delete Driver Error:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.statusMessage ||
          "Failed to delete driver. Please try again.",
        severity: "error",
      });
    }
  };

  function calculateAge(dob) {
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // If birthday hasn't occurred yet this year, reduce age by 1
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  }

  /* ------------------------------------------------------------------
     The app shell renders this screen inside <main>, which carries a 56px
     top margin for the fixed header and sits in a minHeight:100vh flex
     column. A plain height:100vh here therefore overflows by the header
     height and the whole page scrolls. Measure our own distance from the
     top of the document instead and claim only what is actually left, so
     the shell can change without this breaking.
     ------------------------------------------------------------------ */
  const rootRef = useRef(null);
  const [availableHeight, setAvailableHeight] = useState(null);

  useEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      // offsetTop chain is scroll-independent, unlike getBoundingClientRect.
      let top = 0;
      for (let node = el; node; node = node.offsetParent) {
        top += node.offsetTop || 0;
      }
      // Small slack absorbs the shell's own bottom padding.
      setAvailableHeight(`calc(100vh - ${Math.round(top) + 10}px)`);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const filteredDrivers = drivers.filter(
    (driver) =>
      driver.name.toLowerCase().includes(search.toLowerCase()) ||
      driver.code.toLowerCase().includes(search.toLowerCase()) ||
      (driver.phone && driver.phone.includes(search)),
  );
  // const filteredDrivers = drivers;

  return (
    <Box
      ref={rootRef}
      sx={{
        p: 3,
        background: "#f5f7fa",
        boxSizing: "border-box",
        // Claim exactly the space left below the shell header so the two
        // panels scroll independently and the page itself never scrolls.
        height: { xs: "auto", md: availableHeight || "100vh" },
        overflow: { xs: "visible", md: "hidden" },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {" "}
        <Box
          onClick={() => navigate(-1)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: 2,
            bgcolor: "#139fcae8",
            p: 0.5,
          }}
        >
          <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
        </Box>
        <Box
          onClick={() => navigate("/travel/dashboard")}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: 2,
            bgcolor: "#139fcae8",
            p: 0.5,
          }}
        >
          <HouseIcon sx={{ fill: "#FFF" }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Driver Management
        </Typography>
      </Box>

      <Grid
        container
        spacing={3}
        sx={{
          flex: { md: 1 },
          minHeight: 0, // lets the children actually shrink and scroll
          overflow: { xs: "visible", md: "hidden" },
        }}
      >
        {/* LEFT PANEL */}
        <Grid item xs={12} md={4} sx={{ height: { xs: "auto", md: "100%" } }}>
          <Paper
            sx={{
              p: 2,
              height: { xs: "auto", md: "100%" },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleAddDriver}
              sx={{
                mb: 2,
                textTransform: "none",
                background: "#6c63ff",
                color: "#fff",
                "&:hover": { background: "#5a52d6" },
              }}
            >
              Add Driver
            </Button>

            {/* Add + search stay put; only the list below scrolls */}
            <TextField
              fullWidth
              placeholder="Search driver"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ mb: 2, flexShrink: 0 }}
            />

            <TableContainer
              sx={{
                flex: { md: 1 },
                minHeight: 0,
                maxHeight: { xs: "60vh", md: "none" },
                ...HIDDEN_SCROLLBAR,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell sx={{ width: 44 }} align="center">
                      FB
                    </TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredDrivers.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={() => handleDriverSelect(row)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedDriver?.id === row.id
                            ? "#f0f0ff"
                            : "transparent",
                        "&:hover": { backgroundColor: "#f8f8ff" },
                      }}
                    >
                      <TableCell>{row.driver_code}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell sx={{ fontSize: "12px" }}>
                        {row.phone}
                      </TableCell>
                      <TableCell align="center">
                        {(() => {
                          const latest = getLatestFeedback(row);
                          if (!latest) return null;
                          const r = getRating(latest.rating);
                          if (!r) return null;
                          return (
                            <Tooltip
                              title={`${r.label} — ${fmtDMY(latest.date)}${
                                latest.remarks ? `: ${latest.remarks}` : ""
                              }`}
                              arrow
                            >
                              <Box
                                sx={{
                                  width: 11,
                                  height: 11,
                                  borderRadius: "50%",
                                  bgcolor: r.color,
                                  display: "inline-block",
                                }}
                              />
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {row.status === "A" && (
                          <Chip
                            label="Active"
                            size="small"
                            sx={{ background: "#e8f5e9", color: "#2e7d32" }}
                          />
                        )}
                        {row.status === "B" && (
                          <Chip
                            label="Blocked"
                            size="small"
                            sx={{ background: "#e8f5e9", color: "#ff7b00" }}
                          />
                        )}{" "}
                        {row.status === "I" && (
                          <Chip
                            label="Inactive"
                            size="small"
                            sx={{ background: "#e8f5e9", color: "#c62828" }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* RIGHT PANEL */}
        <Grid item xs={12} md={8} sx={{ height: { xs: "auto", md: "100%" } }}>
          <Paper
            sx={{
              p: 3,
              height: { xs: "auto", md: "100%" },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            {/* Header — actions live up here so they stay put while the body
                below scrolls, and never need scrolling to reach. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                mb: 2,
                flexShrink: 0,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {isAddMode ? "Add New Driver" : "Driver Details"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloseIcon />}
                  onClick={handleCancel}
                  sx={{ textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCreateDriver}
                  sx={{
                    textTransform: "none",
                    background: "#6c63ff",
                    "&:hover": { background: "#5a52d6" },
                  }}
                >
                  {isAddMode ? "Save Driver" : "Save Changes"}
                </Button>
                {!isAddMode && selectedDriver && (
                  <Tooltip title="Delete driver" arrow>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={handleDeleteDriver}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Scrollable body */}
            <Box
              sx={{
                flex: { md: 1 },
                minHeight: 0,
                ...HIDDEN_SCROLLBAR,
                // breathing room so focus rings aren't clipped by the edge
                px: 0.25,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Driver Code *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.driver_code}
                    onChange={(e) =>
                      handleInputChange("driver_code", e.target.value)
                    }
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Name *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Phone *
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    // label="Phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ""); // only digits
                      if (value.length <= 10) {
                        handleInputChange("phone", value);
                      }
                    }}
                    required
                    error={
                      formData.phone.length > 0 && formData.phone.length !== 10
                    }
                    helperText={
                      formData.phone.length > 0 && formData.phone.length !== 10
                        ? "Mobile number must be exactly 10 digits"
                        : ""
                    }
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                </Grid>

                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    DOB
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleInputChange("dob", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Age
                  </Typography>
                  <TextField
                    disabled
                    fullWidth
                    size="small"
                    value={calculateAge(formData?.dob) || 0}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Blood Group
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    select
                    value={formData.bloodGroup}
                    onChange={(e) =>
                      handleInputChange("bloodGroup", e.target.value)
                    }
                  >
                    {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                      (group) => (
                        <MenuItem key={group} value={group}>
                          {group}
                        </MenuItem>
                      ),
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Experience in years
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={formData.experience}
                    onChange={(e) =>
                      handleInputChange("experience", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Qualification
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.qualification}
                    onChange={(e) =>
                      handleInputChange("qualification", e.target.value)
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Joined Date
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={formData.joinedDate || ""}
                    onChange={(e) =>
                      handleInputChange("joinedDate", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              {/* Languages and Status in same row - COMPACT DESIGN */}
              <Grid
                container
                spacing={2}
                alignItems="flex-start"
                sx={{ mt: 1 }}
              >
                {/* Languages input only */}
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Languages
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 0.6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add language"
                      value={languageInput}
                      onChange={(e) => setLanguageInput(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleAddLanguage()
                      }
                    />
                    <Button
                      variant="outlined"
                      onClick={handleAddLanguage}
                      size="small"
                      sx={{ minWidth: "70px" }}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>

                {/* Assign Vehicle */}
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Assign Vehicle
                  </Typography>
                  <Autocomplete
                    sx={{ mt: 0.5 }}
                    size="small"
                    options={vehicles}
                    loading={isLoadingVehicles}
                    getOptionLabel={(option) => option.vehicle_code || ""}
                    value={selectedVehicle}
                    onChange={(e, newValue) => {
                      setSelectedVehicle(newValue);
                      handleInputChange(
                        "vehicle_id",
                        newValue?.vehicle_id || null,
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select vehicle"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingVehicles && (
                                <CircularProgress size={18} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Status */}
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                    <Select
                      value={formData.status}
                      onChange={(e) => {
                        handleInputChange("status", e.target.value);
                        if (e.target.value !== "B") {
                          handleInputChange("blockedFrom", "");
                          handleInputChange("blockedTo", "");
                        }
                      }}
                    >
                      <MenuItem value="A">Active</MenuItem>
                      <MenuItem value="I">Inactive</MenuItem>
                      <MenuItem value="B">Blocked</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Languages Chips Display */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  mt: 1,
                  mb: 1,
                }}
              >
                {formData?.languages?.map((lang, index) => (
                  <Chip
                    key={index}
                    label={lang}
                    onDelete={() => handleDeleteLanguage(lang)}
                    size="small"
                  />
                ))}
              </Box>

              {/* Blocked dates - shown only when status is B */}
              {formData.status === "B" && (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Blocked From
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={formData.blockedFrom}
                      onChange={(e) =>
                        handleInputChange("blockedFrom", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Blocked To
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      value={formData.blockedTo}
                      onChange={(e) =>
                        handleInputChange("blockedTo", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              {/* DOCUMENT TABLE */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Documents
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Document Name</TableCell>
                      <TableCell>Number</TableCell>
                      <TableCell>File</TableCell>
                      <TableCell>Renewal Date</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData?.documents?.map((doc) => (
                      <TableRow key={doc.doc_id || doc.id}>
                        <TableCell>{doc.name}</TableCell>
                        <TableCell>{doc.number}</TableCell>
                        <TableCell sx={{ color: "#6c63ff", fontSize: "12px" }}>
                          {doc.file || "N/A"}
                        </TableCell>
                        <TableCell>{doc.renewalDate}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditDocument(doc)}
                            sx={{ mr: 0.5 }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDeleteDocument(doc.doc_id || doc.id)
                            }
                          >
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Document name"
                          value={newDocument.name}
                          onChange={(e) =>
                            handleDocumentChange("name", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Number"
                          value={newDocument.number}
                          onChange={(e) =>
                            handleDocumentChange("number", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          fullWidth
                          sx={{
                            textTransform: "none",
                            fontSize: "12px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {newDocument.fileName || "Upload File"}
                          <input
                            type="file"
                            hidden
                            accept="application/pdf,image/*"
                            onChange={(e) =>
                              handleDocumentChange("file", e.target.files[0])
                            }
                          />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          value={newDocument.renewalDate}
                          onChange={(e) =>
                            handleDocumentChange("renewalDate", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </TableCell>
                      <TableCell>
                        {editingDocId ? (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleUpdateDocument}
                              disabled={
                                !newDocument.name ||
                                !newDocument.number ||
                                !newDocument.renewalDate
                              }
                              sx={{ mr: 0.5 }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={handleCancelEditDocument}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleAddDocument}
                            disabled={
                              !newDocument.name ||
                              !newDocument.number ||
                              !newDocument.renewalDate
                            }
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 3 }} />

              {/* ── FEEDBACK ───────────────────────────────────────────
                Fixed height: the entry form stays pinned at the top and the
                history scrolls inside itself, so the page never grows as
                feedback accumulates and Save stays reachable.
            ─────────────────────────────────────────────────────────── */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Feedback
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                  >
                    {feedbackStats.total === 0
                      ? "Rate the driver so problems are visible without reading"
                      : `${feedbackStats.total} ${
                          feedbackStats.total === 1 ? "entry" : "entries"
                        }`}
                  </Typography>
                </Box>

                {/* Average — the one-glance verdict */}
                {feedbackStats.total > 0 && feedbackStats.averageRating && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                    >
                      Average
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 0.9,
                        py: 0.3,
                        borderRadius: 1,
                        bgcolor: `${feedbackStats.averageRating.color}14`,
                        border: "1px solid",
                        borderColor: `${feedbackStats.averageRating.color}55`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 9,
                          height: 9,
                          borderRadius: "50%",
                          bgcolor: feedbackStats.averageRating.color,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          color: feedbackStats.averageRating.color,
                        }}
                      >
                        {feedbackStats.averageRating.label}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        ({feedbackStats.average})
                      </Typography>
                    </Box>

                    <Tooltip title="Export feedback to Excel" arrow>
                      <IconButton
                        size="small"
                        onClick={handleFeedbackExcelExport}
                        sx={{
                          bgcolor: "#3873f3",
                          color: "#fff",
                          p: 0.5,
                          "&:hover": { bgcolor: "#2a5fd0" },
                        }}
                      >
                        <IosShareIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              {/* ENTRY FORM — pinned above the history so adding never
                requires scrolling past existing entries. */}
              <Box
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: editingFeedbackId
                    ? "#6c63ff"
                    : "rgba(0,0,0,0.12)",
                  bgcolor: editingFeedbackId
                    ? "rgba(108,99,255,0.04)"
                    : "#fafafa",
                  transition: "all 0.15s",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ width: 160, flexShrink: 0 }}>
                    <SingleDatePicker
                      date={newFeedback.date}
                      onDateChange={(val) => handleFeedbackChange("date", val)}
                      label="Pick date"
                      maxDate={toYMD(new Date())}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 260 }}>
                    <RatingSelector
                      value={newFeedback.rating}
                      onChange={(val) => handleFeedbackChange("rating", val)}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    mt: 1.2,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={1}
                    maxRows={4}
                    placeholder="Write the feedback (optional)"
                    value={newFeedback.remarks}
                    onChange={(e) =>
                      handleFeedbackChange("remarks", e.target.value)
                    }
                    inputProps={{ style: { fontSize: "0.85rem" } }}
                    sx={{ bgcolor: "#fff" }}
                  />
                  {editingFeedbackId ? (
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleUpdateFeedback}
                        disabled={!isFeedbackComplete}
                        sx={{
                          textTransform: "none",
                          background: "#6c63ff",
                          "&:hover": { background: "#5a52d6" },
                        }}
                      >
                        Update
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={resetFeedbackEntry}
                        sx={{ textTransform: "none" }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={handleAddFeedback}
                      disabled={!isFeedbackComplete}
                      sx={{
                        textTransform: "none",
                        flexShrink: 0,
                        background: "#6c63ff",
                        "&:hover": { background: "#5a52d6" },
                      }}
                    >
                      Add
                    </Button>
                  )}
                </Box>
              </Box>

              {/* FILTER STRIP — jump straight to the bad ones */}
              {feedbackStats.total > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Box
                    onClick={() => setRatingFilter(null)}
                    sx={{
                      px: 1,
                      py: 0.3,
                      borderRadius: 1,
                      cursor: "pointer",
                      border: "1px solid",
                      borderColor:
                        ratingFilter === null ? "#6c63ff" : "rgba(0,0,0,0.12)",
                      bgcolor:
                        ratingFilter === null
                          ? "rgba(108,99,255,0.08)"
                          : "transparent",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        color:
                          ratingFilter === null ? "#6c63ff" : "text.secondary",
                      }}
                    >
                      All {feedbackStats.total}
                    </Typography>
                  </Box>
                  {[...RATING_SCALE]
                    .slice()
                    .reverse()
                    .filter((r) => feedbackStats.counts[r.value] > 0)
                    .map((r) => {
                      const active = ratingFilter === r.value;
                      return (
                        <Tooltip
                          key={r.value}
                          title={`Show only ${r.label}`}
                          arrow
                        >
                          <Box
                            onClick={() =>
                              setRatingFilter(active ? null : r.value)
                            }
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4,
                              px: 1,
                              py: 0.3,
                              borderRadius: 1,
                              cursor: "pointer",
                              border: "1px solid",
                              borderColor: active
                                ? r.color
                                : "rgba(0,0,0,0.12)",
                              bgcolor: active ? `${r.color}14` : "transparent",
                              "&:hover": { borderColor: r.color },
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: r.color,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                fontWeight: active ? 700 : 500,
                                color: active ? r.color : "text.secondary",
                              }}
                            >
                              {r.label} {feedbackStats.counts[r.value]}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                </Box>
              )}

              {/* HISTORY — scrolls internally, page height stays constant */}
              {feedbackStats.total === 0 ? (
                <Box
                  sx={{
                    py: 2.5,
                    textAlign: "center",
                    borderRadius: 1.5,
                    border: "1px dashed rgba(0,0,0,0.15)",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "0.82rem", color: "text.secondary" }}
                  >
                    No feedback yet
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    maxHeight: 260,
                    overflowY: "auto",
                    borderRadius: 1.5,
                    border: "1px solid rgba(0,0,0,0.1)",
                    // Slim scrollbar so it doesn't dominate a narrow list
                    "&::-webkit-scrollbar": { width: 7 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.22)",
                      borderRadius: 4,
                    },
                  }}
                >
                  {visibleFeedbacks.map((fb, idx) => {
                    const fbId = fb.feedback_id || fb.id;
                    const isEditingThis = editingFeedbackId === fbId;
                    const r = getRating(fb.rating);
                    return (
                      <Box
                        key={fbId}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.2,
                          px: 1.2,
                          py: 1,
                          borderTop: idx === 0 ? "none" : "1px solid",
                          borderColor: "rgba(0,0,0,0.06)",
                          // Colour-coded left edge: scan sentiment down the list
                          borderLeft: "3px solid",
                          borderLeftColor: r ? r.color : "transparent",
                          bgcolor: isEditingThis
                            ? "rgba(108,99,255,0.06)"
                            : "transparent",
                          "&:hover": {
                            bgcolor: isEditingThis
                              ? "rgba(108,99,255,0.06)"
                              : "rgba(0,0,0,0.02)",
                          },
                          "&:hover .fb-actions": { opacity: 1 },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            color: "text.secondary",
                            width: 82,
                            flexShrink: 0,
                            pt: 0.1,
                          }}
                        >
                          {fmtDMY(fb.date) || "-"}
                        </Typography>

                        <Box sx={{ width: 92, flexShrink: 0, pt: 0.1 }}>
                          <RatingChip value={fb.rating} />
                        </Box>

                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: "0.85rem",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            color: fb.remarks
                              ? "text.primary"
                              : "text.disabled",
                            minWidth: 0,
                          }}
                        >
                          {fb.remarks || "No remarks"}
                        </Typography>

                        {/* Revealed on hover — keeps the list quiet to read */}
                        <Box
                          className="fb-actions"
                          sx={{
                            display: "flex",
                            gap: 0.2,
                            flexShrink: 0,
                            opacity: isEditingThis ? 1 : 0,
                            transition: "opacity 0.12s",
                          }}
                        >
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditFeedback(fb)}
                              sx={{ p: 0.4 }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteFeedback(fbId)}
                              sx={{ p: 0.4 }}
                            >
                              <DeleteOutlineOutlinedIcon
                                sx={{ fontSize: 16 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    );
                  })}

                  {visibleFeedbacks.length === 0 && (
                    <Box sx={{ py: 2, textAlign: "center" }}>
                      <Typography
                        sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                      >
                        No entries with this rating
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            {/* end scrollable body */}
          </Paper>
        </Grid>
      </Grid>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={Boolean(discardPrompt)}
        onClose={() => setDiscardPrompt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, fontSize: "1rem", fontWeight: 700 }}>
          Unsaved changes
        </DialogTitle>
        <DialogContent sx={{ pb: 1.5 }}>
          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
            {hasPendingDocument
              ? "The document fields have details that were never added to the list."
              : hasPendingFeedback
              ? "The feedback fields have details that were never added to the list."
              : "This driver has changes that were never saved."}{" "}
            {discardPrompt?.actionLabel
              ? `Discard them and ${discardPrompt.actionLabel}?`
              : "Discard them?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button
            onClick={() => setDiscardPrompt(null)}
            size="small"
            sx={{ textTransform: "none" }}
          >
            Keep editing
          </Button>
          <Button
            onClick={handleDiscardConfirm}
            color="error"
            variant="contained"
            size="small"
            sx={{ textTransform: "none" }}
          >
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete driver "{selectedDriver?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
